<template>
    <div
        v-if="show"
        class="tw:fixed tw:inset-0 tw:z-50 tw:flex tw:items-center tw:justify-center tw:p-2 tw:sm:p-4 tw:md:p-6"
    >
        <!-- Backdrop -->
        <div
            class="tw:absolute tw:inset-0 tw:bg-gray-400/50 tw:backdrop-blur-sm"
            @click="onBackdropClick"
        ></div>

        <!-- Panel -->
        <div
            role="dialog"
            :aria-labelledby="titleId"
            :aria-describedby="messageId"
            aria-modal="true"
            :class="panelClasses"
        >
            <!-- Header -->
            <div class="tw:flex tw:items-start tw:justify-between tw:p-4 tw:border-b tw:border-gray-100">
                <div class="tw:flex-1 tw:pr-4">
                    <slot name="header">
                        <h2 :id="titleId" class="tw:text-xl tw:font-semibold tw:text-gray-800 tw:mb-0">
                            {{ title }}
                        </h2>
                    </slot>
                    <p v-if="subtitle" class="tw:m-0 tw:mt-1 tw:text-sm tw:text-gray-500">
                        {{ subtitle }}
                    </p>
                </div>
                <button
                    type="button"
                    :aria-label="cerrarLabel"
                    class="tw:p-2 tw:pb-0 tw:rounded tw:text-gray-400 tw:hover:text-gray-600 tw:hover:bg-gray-100 tw:transition-colors"
                    @click="onClose"
                >
                    <i class="material-icons tw:text-xl tw:leading-none">close</i>
                </button>
            </div>

            <!-- Body -->
            <div :id="messageId" :class="`tw:min-h-0 tw:flex-1 tw:p-2 tw:sm:p-4 ${bodyScrollable ? 'tw:overflow-y-auto' : 'tw:overflow-hidden tw:flex tw:flex-col'} ${customMesageContainerClasses}`">
                <p v-if="message" class="tw:text-base tw:text-gray-700">
                    {{ message }}
                </p>
                <slot></slot>
            </div>

            <!-- Footer -->
            <div class="tw:flex tw:items-center tw:justify-end tw:gap-3 tw:p-4 tw:px-6 tw:bg-gray-50">
                <Button
                    v-if="showCancel"
                    variant="default"
                    :disabled="loading"
                    :data-testid="cancelTestId || null"
                    @click="onClose"
                >
                    {{ cancelText }}
                </Button>
                <Button
                    v-if="showConfirm"
                    type="button"
                    :variant="confirmVariant"
                    :disabled="confirmDisabled"
                    :loading="loading"
                    :data-testid="confirmTestId || null"
                    @click="onConfirm"
                >
                    <slot name="confirm">
                        {{ confirmText }}
                    </slot>
                </Button>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { computed, getCurrentInstance, onMounted, onUnmounted, watch } from 'vue';
    import { t } from '@/utils/i18n-helper';
    import Button from '@/modules/front/ui/Button.vue';

    const props = defineProps({
        show: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            default: '',
        },
        subtitle: {
            type: String,
            default: '',
        },
        message: {
            type: String,
            default: '',
        },
        confirmText: {
            type: String,
            default: t('formulario_pago.confirmar'),
        },
        cancelText: {
            type: String,
            default: t('formulario_pago.cancelar'),
        },
        customMesageContainerClasses: {
            type: String,
            default: '',
        },
        /**
         * Variante visual del botón de confirmación.
         * 'danger'   → rojo  (destructive, default)
         * 'warning'  → amarillo
         * 'primary'  → azul
         */
        confirmVariant: {
            type: String,
            default: 'alt',
            validator: value => ['primary', 'danger', 'warning', 'dark', 'success', 'ghost', 'default', 'alt'].includes(value),
        },
        showCancel: {
            type: Boolean,
            default: true,
        },
        /**
         * Muestra/oculta el botón de confirmación.
         * Útil cuando el modal solo necesita botón de cancelar (ej: 3DS challenge).
         */
        showConfirm: {
            type: Boolean,
            default: true,
        },
        confirmDisabled: {
            type: Boolean,
            default: false,
        },
        closeOnBackdrop: {
            type: Boolean,
            default: true,
        },
        closeOnEscape: {
            type: Boolean,
            default: true,
        },
        /**
         * Cuando es `false`, el body del modal no scrollea por sí mismo.
         * Útil cuando el slot gestiona su propio layout scrollable interno
         * (e.g. lista + paginación fija).
         */
        bodyScrollable: {
            type: Boolean,
            default: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        /**
         * data-testid para el botón de confirmación.
         */
        confirmTestId: {
            type: String,
            default: '',
        },
        /**
         * data-testid para el botón de cancelación.
         */
        cancelTestId: {
            type: String,
            default: '',
        },
        /**
         * Tamaño del panel del modal.
         * 'sm' → compacto (default)
         * 'md' → medio
         * 'lg' → grande (ideal para contenido denso como términos + plazos)
         */
        size: {
            type: String,
            default: 'sm',
            validator: value => ['sm', 'md', 'lg'].includes(value),
        },
    });

    const emit = defineEmits(['close', 'confirm', 'x-close']);
    const cerrarLabel = t('formulario_pago.cerrar');
    const BODY_SCROLL_LOCK_COUNT_ATTR = 'data-front-modal-lock-count';
    const BODY_SCROLL_PREV_OVERFLOW_ATTR = 'data-front-modal-prev-overflow';
    const BODY_SCROLL_PREV_TOUCH_ACTION_ATTR = 'data-front-modal-prev-touch-action';

    let isBodyScrollLocked = false;

    // ---------------------------------------------------------------------------
    // IDs para accesibilidad (aria-labelledby / aria-describedby)
    // Únicos por instancia — evita duplicados en el DOM cuando hay N modales
    // montados simultáneamente (aunque sólo uno esté visible).
    // ---------------------------------------------------------------------------
    const uid = getCurrentInstance()?.uid ?? Math.random().toString(36).slice(2);
    const titleId   = `base-modal-front-title-${uid}`;
    const messageId = `base-modal-front-message-${uid}`;

    // ---------------------------------------------------------------------------
    // Tamaño del panel según prop `size`
    // ---------------------------------------------------------------------------
    const sizePresets = {
        sm: 'tw:max-h-2/3 tw:lg:w-2/5',
        md: 'tw:max-h-[80vh] tw:lg:w-1/2',
        lg: 'tw:max-h-[90vh] tw:lg:w-3/5',
    };

    const panelClasses = computed(() => {
        const sizeClass = sizePresets[props.size] ?? sizePresets.sm;
        return [
            'tw:relative tw:flex tw:w-full tw:flex-col tw:overflow-hidden tw:bg-white tw:rounded-xl tw:shadow-2xl',
            sizeClass,
        ].join(' ');
    });

    // ---------------------------------------------------------------------------
    // Handlers
    // ---------------------------------------------------------------------------
    const onClose = () => emit('close');
    const onConfirm = () => {
        if (!props.confirmDisabled) {
            emit('confirm');
        }
    };
    const onBackdropClick = () => {
        if (props.closeOnBackdrop) {
            onClose();
        }
    };

    const handleKeydown = event => {
        if (event.key === 'Escape' && props.closeOnEscape && props.show) {
            onClose();
        }
    };

    const lockBodyScroll = () => {
        const body = document.body;
        const currentCount = Number(body.getAttribute(BODY_SCROLL_LOCK_COUNT_ATTR) ?? '0');

        if (currentCount === 0) {
            body.setAttribute(BODY_SCROLL_PREV_OVERFLOW_ATTR, body.style.overflow || '');
            body.setAttribute(BODY_SCROLL_PREV_TOUCH_ACTION_ATTR, body.style.touchAction || '');
            body.style.overflow = 'hidden';
            body.style.touchAction = 'none';
        }

        body.setAttribute(BODY_SCROLL_LOCK_COUNT_ATTR, String(currentCount + 1));
    };

    const unlockBodyScroll = () => {
        const body = document.body;
        const currentCount = Number(body.getAttribute(BODY_SCROLL_LOCK_COUNT_ATTR) ?? '0');

        if (currentCount <= 1) {
            body.style.overflow = body.getAttribute(BODY_SCROLL_PREV_OVERFLOW_ATTR) || '';
            body.style.touchAction = body.getAttribute(BODY_SCROLL_PREV_TOUCH_ACTION_ATTR) || '';
            body.removeAttribute(BODY_SCROLL_LOCK_COUNT_ATTR);
            body.removeAttribute(BODY_SCROLL_PREV_OVERFLOW_ATTR);
            body.removeAttribute(BODY_SCROLL_PREV_TOUCH_ACTION_ATTR);
            return;
        }

        body.setAttribute(BODY_SCROLL_LOCK_COUNT_ATTR, String(currentCount - 1));
    };

    const syncBodyScrollLock = isShown => {
        if (isShown && !isBodyScrollLocked) {
            lockBodyScroll();
            isBodyScrollLocked = true;
            return;
        }

        if (!isShown && isBodyScrollLocked) {
            unlockBodyScroll();
            isBodyScrollLocked = false;
        }
    };

    watch(
        () => props.show,
        isShown => {
            syncBodyScrollLock(isShown);
        },
        { immediate: true }
    );

    onMounted(() => {
        document.addEventListener('keydown', handleKeydown);
    });

    onUnmounted(() => {
        syncBodyScrollLock(false);
        document.removeEventListener('keydown', handleKeydown);
    });
</script>
